// src/components/sections/ServicesSection.js
import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaFemale, FaBaby, FaStethoscope,
    FaClinicMedical, FaHeartbeat, FaAmbulance,
    FaMicroscope, FaProcedures, FaArrowRight
} from 'react-icons/fa';

const ServicesSection = () => {
    const services = [
        {
            icon: <FaFemale />,
            title: "Women's Health & Gynecology",
            description: "Comprehensive women's healthcare from adolescence through menopause.",
            color: '#20B2AA'
        },
        {
            icon: <FaBaby />,
            title: "Pregnancy & Maternity Care",
            description: "Complete prenatal, delivery, and postnatal care for mothers.",
            color: '#FF7F50'
        },
        {
            icon: <FaStethoscope />,
            title: "Fertility & Infertility Treatments",
            description: "Advanced fertility treatments including IVF and reproductive care.",
            color: '#87CEEB'
        },
        {
            icon: <FaClinicMedical />,
            title: "Pediatric & Neonatal Care",
            description: "Specialized care for newborns, infants, and children.",
            color: '#FFD700'
        },
        {
            icon: <FaProcedures />,
            title: "Surgical & Laparoscopic Procedures",
            description: "Minimally invasive surgeries for faster recovery.",
            color: '#9370DB'
        },
        {
            icon: <FaMicroscope />,
            title: "Diagnostics & Imaging",
            description: "Advanced ultrasound, MRI, and laboratory services.",
            color: '#3CB371'
        },
        {
            icon: <FaHeartbeat />,
            title: "Comprehensive Wellness",
            description: "Preventive care and health maintenance programs.",
            color: '#FF6347'
        },
        {
            icon: <FaAmbulance />,
            title: "Emergency & 24/7 Care",
            description: "Round-the-clock emergency medical services.",
            color: '#DC143C'
        },
    ];

    return (
        <section id="services" className="section-padding">
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Our Comprehensive Services
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        From routine check-ups to specialized treatments, we provide complete healthcare
                        solutions for women, mothers, and children.
                    </p>
                </motion.div>

                <Row className="g-4">
                    {services.map((service, index) => (
                        <Col lg={3} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5 }}
                            >
                                <Card className="healthcare-card h-100 border-0">
                                    <Card.Body className="text-center p-4">
                                        <div
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                backgroundColor: `${service.color}15`,
                                                color: service.color,
                                                fontSize: '2rem',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            {service.icon}
                                        </div>
                                        <Card.Title className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                                            {service.title}
                                        </Card.Title>
                                        <Card.Text style={{ color: '#666', fontSize: '0.9rem' }}>
                                            {service.description}
                                        </Card.Text>
                                    </Card.Body>
                                    <Card.Footer className="border-0 bg-transparent text-center pb-4 pt-0">
                                        <motion.div
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <Button
                                                variant="link"
                                                className="text-decoration-none d-inline-flex align-items-center justify-content-center gap-2"
                                                style={{
                                                    color: service.color,
                                                    fontWeight: '600',
                                                    fontSize: '0.9rem',
                                                    padding: '8px 16px',
                                                    borderRadius: '25px',
                                                    backgroundColor: `${service.color}10`,
                                                    border: `1px solid ${service.color}30`,
                                                    transition: 'all 0.3s ease',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <span style={{ position: 'relative', zIndex: 2 }}>
                                                    Learn More
                                                </span>
                                                <motion.span
                                                    initial={{ x: 0 }}
                                                    whileHover={{ x: 5 }}
                                                    transition={{ type: "spring", stiffness: 400 }}
                                                    style={{ position: 'relative', zIndex: 2 }}
                                                >
                                                    <FaArrowRight style={{ fontSize: '0.8rem' }} />
                                                </motion.span>
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: '-100%',
                                                        width: '100%',
                                                        height: '100%',
                                                        background: `linear-gradient(90deg, transparent, ${service.color}20, transparent)`,
                                                        transition: 'left 0.6s ease'
                                                    }}
                                                />
                                            </Button>
                                        </motion.div>
                                    </Card.Footer>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                <style jsx="true">{`
                    /* Button hover effects */
                    .healthcare-card:hover .btn-link {
                        background-color: ${service => service.color}20 !important;
                        border-color: ${service => service.color}50 !important;
                    }
                    
                    .healthcare-card:hover .btn-link span:last-child {
                        left: 100%;
                    }
                    
                    /* Icon hover effect */
                    .healthcare-card:hover .rounded-circle {
                        transform: scale(1.1);
                        box-shadow: 0 5px 15px rgba(0,0,0,0.1);
                    }
                    
                    /* Card hover effect */
                    .healthcare-card {
                        transition: all 0.3s ease;
                    }
                    
                    .healthcare-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }
                    
                    /* Mobile responsive adjustments */
                    @media (max-width: 768px) {
                        .btn-link {
                            padding: 6px 12px !important;
                            font-size: 0.85rem !important;
                        }
                    }
                `}</style>
            </Container>
        </section>
    );
};

export default ServicesSection;